import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.jsx';
import TierBadge from '../../components/TierBadge.jsx';
import { Link } from 'react-router-dom';
import { useTier } from '../../lib/useTier.js';
import AiUsageWidget from '../../components/AiUsageWidget.jsx';

const DEFAULTS = {
  availabilityModel: 'opt-out',
  weeklyConfirmationEnabled: true,
  weeklyDigestEmailEnabled: true,
  gapPushEnabled: true,
  timezone: '',
  quietHours: { start: 20, end: 8, enabled: true },
  feedbackEmail: '',
};

const HOURS_24 = Array.from({ length: 24 }, (_, i) => i);
function formatHour(h) { return `${String(h).padStart(2, '0')}:00`; }

function getTimezoneList() {
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf('timeZone');
    }
  } catch { /* fall through */ }
  return [
    'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 'Australia/Adelaide',
    'Australia/Perth', 'Australia/Hobart', 'Australia/Darwin',
    'Pacific/Auckland', 'Pacific/Wellington', 'UTC',
  ];
}

function detectBrowserTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { return ''; }
}

export default function Settings() {
  const { claims } = useAuth();
  const vendorId = claims?.vendor_id;
  const { tier } = useTier();
  const quietHoursUnlocked = !!tier?.features?.quietHoursForwarding;

  const [vendor, setVendor] = useState(null);
  const [error, setError] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [savedKey, setSavedKey] = useState(null);

  useEffect(() => {
    if (!vendorId) return;
    const unsub = onSnapshot(
      doc(db, 'vendors', vendorId),
      (snap) => setVendor(snap.exists() ? snap.data() : {}),
      (err) => setError(err.message),
    );
    return () => unsub();
  }, [vendorId]);

  function val(key) {
    if (!vendor) return DEFAULTS[key];
    return key in vendor ? vendor[key] : DEFAULTS[key];
  }

  // Priority rules live as one nested object on the vendor doc; default to
  // disabled so the feature is invisible until an admin opts in.
  const priorityRules = (vendor && vendor.priorityRules) || { enabled: false, windows: {} };

  function flashSaved(key) {
    setSavedKey(key);
    setTimeout(() => setSavedKey((c) => (c === key ? null : c)), 1800);
  }

  async function setAvailabilityModel(model) {
    setError(null); setSavingKey('availabilityModel');
    try { await httpsCallable(functions, 'setAvailabilityModel')({ model }); flashSaved('availabilityModel'); }
    catch (err) { setError(err.message || String(err)); }
    finally { setSavingKey(null); }
  }

  async function updateOne(key, value) {
    setError(null); setSavingKey(key);
    try { await httpsCallable(functions, 'updateVendorSettings')({ patch: { [key]: value } }); flashSaved(key); }
    catch (err) { setError(err.message || String(err)); }
    finally { setSavingKey(null); }
  }

  if (vendor === null) {
    return (<><h2>Settings</h2><div className="placeholder">Loading...</div></>);
  }

  const browserTz = detectBrowserTimezone();
  const currentTz = val('timezone') || '';

  return (
    <>
      <h2>Settings</h2>
      <PlanIndicator />
      {error && <div className="error-banner">{error}</div>}

      <section className="settings-section">
        <h3>Assets &amp; QR stickers</h3>
        <p className="settings-help">
          Each piece of equipment customers can report problems with has its own QR
          sticker. Assets moved to their own page so the list stays manageable as it grows.
        </p>
        <Link to="/admin/assets" className="btn-primary" style={{
          textDecoration: 'none', display: 'inline-block',
        }}>
          Manage assets →
        </Link>
      </section>

      {/* Task #106 — AI add-on: single vendor-level toggle. Unlocks customer-side
          triage chat + responder-side AI assistant. Flat $15/month. */}
      <section className="settings-section">
        <h3>
          AI add-on
          <span style={{
            marginLeft: 8, fontSize: 12, fontWeight: 600,
            padding: '2px 8px', borderRadius: 999,
            background: '#ede9fe', color: '#5b21b6',
          }}>+$15/month</span>
        </h3>
        <p className="settings-help">
          Turn on AI to give every customer a quick triage chat before dispatch and
          give your techs an in-app assistant grounded in your uploaded manuals.
          Charged as a flat add-on, on top of your tier. Turn off anytime.
        </p>
        <label className="settings-toggle">
          <input type="checkbox"
            checked={val('aiAddon')?.enabled === true}
            onChange={(e) => updateOne('aiAddon', { enabled: e.target.checked })}
            disabled={savingKey === 'aiAddon'} />
          <span>Enable AI add-on (+$15 / month)</span>
        </label>
        {savedKey === 'aiAddon' && <span className="settings-saved">Saved</span>}

        {val('aiAddon')?.enabled === true && (
          <AiUsageWidget vendorId={vendorId} />
        )}

        {val('aiAddon')?.enabled === true && (
          <div style={{
            marginTop: 12, padding: 12,
            background: '#f8fafc', borderRadius: 8,
            display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Next steps to get the most out of AI</div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>
              1. Configure a <Link to="/admin/skills">playbook per skill</Link> so the
              customer-side AI knows what to ask.
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>
              2. Upload your equipment manuals to the{' '}
              <Link to="/admin/knowledge">Knowledge Base</Link> so the technician
              assistant can quote them on jobs.
            </div>
          </div>
        )}

        <p style={{ marginTop: 12, fontSize: 11, color: 'var(--muted)' }}>
          Note: billing happens through your subscription. Toggling here changes
          access immediately; the $15 line item will appear on your next invoice
          once Stripe billing is wired up.
        </p>
      </section>

      {/* Ticket priority (2026-08-29). Work orders arriving by email carry a
          priority; the phone-in and QR forms can set one too. When this is on,
          an urgent ticket gets a SHORTER fuse before it escalates to L2 —
          same team, same chain, just less patience. Off = priority is recorded
          and displayed but dispatch timing never changes. */}
      <section className="settings-section">
        <h3>Ticket priority</h3>
        <p className="settings-help">
          Give urgent jobs a shorter fuse. When a ticket is marked critical or high,
          level 1 gets less time to answer before it escalates — instead of your team's
          normal response window. Leave this off and priority is just a label.
        </p>
        <label className="settings-toggle">
          <input
            type="checkbox"
            checked={priorityRules.enabled === true}
            disabled={savingKey === 'priorityRules'}
            onChange={(e) => updateOne('priorityRules', { ...priorityRules, enabled: e.target.checked })}
          />
          <span><strong>Let priority shorten the response window</strong></span>
        </label>

        {priorityRules.enabled === true && (
          <div style={{ marginTop: 10 }}>
            <table className="data-table" style={{ maxWidth: 460 }}>
              <thead>
                <tr><th>Priority</th><th>Level 1 has…</th></tr>
              </thead>
              <tbody>
                {[
                  ['critical', 'Critical / P1'],
                  ['high', 'High / P2'],
                  ['normal', 'Normal / P3'],
                  ['low', 'Low / P4'],
                ].map(([key, label]) => (
                  <tr key={key}>
                    <td>{label}</td>
                    <td>
                      <select
                        value={(priorityRules.windows || {})[key] ?? ''}
                        disabled={savingKey === 'priorityRules'}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const windows = { ...(priorityRules.windows || {}) };
                          if (raw === '') delete windows[key]; else windows[key] = Number(raw);
                          updateOne('priorityRules', { ...priorityRules, windows });
                        }}
                      >
                        <option value="">Use the team's normal window</option>
                        <option value="60">1 minute</option>
                        <option value="120">2 minutes</option>
                        <option value="180">3 minutes</option>
                        <option value="300">5 minutes</option>
                        <option value="600">10 minutes</option>
                        <option value="1800">30 minutes</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="settings-help" style={{ marginTop: 6 }}>
              Only level 1 is affected — later levels keep the windows set on the team's
              escalation chain.
            </p>
          </div>
        )}
        {savedKey === 'priorityRules' && <span className="settings-saved">Saved</span>}
      </section>

      {/* Operating mode (chosen at signup; can change here). Drives People-page
          labels + helps decide sensible defaults. */}
      <section className="settings-section">
        <h3>Operating mode</h3>
        <p className="settings-help">
          How you use Giraops. Changes the labels on the People page and a few
          defaults — doesn't migrate any existing users or tickets.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
          <label className="settings-toggle">
            <input type="radio" name="operatingMode" value="service"
              checked={(val('operatingMode') || 'service') === 'service'}
              onChange={() => updateOne('operatingMode', 'service')}
              disabled={savingKey === 'operatingMode'} />
            <span><strong>Service business</strong> — our techs respond to calls from our customers.</span>
          </label>
          <label className="settings-toggle">
            <input type="radio" name="operatingMode" value="internal"
              checked={val('operatingMode') === 'internal'}
              onChange={() => updateOne('operatingMode', 'internal')}
              disabled={savingKey === 'operatingMode'} />
            <span><strong>Internal operation</strong> — our staff report breakdowns; our techs/contractors respond.</span>
          </label>
        </div>
        {savedKey === 'operatingMode' && <span className="settings-saved">Saved</span>}
      </section>

      {/* Task #109 — Internal-team mode. Hides customer-facing intake; tickets
          come from admin manual dispatch only. */}
      <section className="settings-section">
        <h3>Internal-team mode</h3>
        <p className="settings-help">
          For teams that dispatch their own crew (no external customers). When ON,
          the customer signup, QR-scan public intake, and self-service ticket
          form are all hidden. Tickets come exclusively from admin manual dispatch
          (Tickets → "+ New ticket (phone-in)").
        </p>
        <label className="settings-toggle">
          <input type="checkbox"
            checked={val('internalOnly') === true}
            onChange={(e) => updateOne('internalOnly', e.target.checked)}
            disabled={savingKey === 'internalOnly'} />
          <span>Disable requester-facing intake</span>
        </label>
        {savedKey === 'internalOnly' && <span className="settings-saved">Saved</span>}
        {val('internalOnly') === true && (
          <p style={{ marginTop: 8, fontSize: 12, color: '#92400e', background: '#fef3c7', padding: '8px 10px', borderRadius: 6 }}>
            Requester-facing intake is OFF. All tickets must be created by an admin.
          </p>
        )}
      </section>

      <section className="settings-section">
        <h3>Availability model</h3>
        <p className="settings-help">
          Determines how on-call availability is tracked across your team.
        </p>
        <label className="settings-radio">
          <input type="radio" name="availabilityModel"
            checked={val('availabilityModel') === 'opt-out'}
            onChange={() => setAvailabilityModel('opt-out')}
            disabled={savingKey === 'availabilityModel'} />
          <div>
            <div className="settings-radio-label">Opt-out (recommended for small teams)</div>
            <div className="settings-radio-help">
              Responders are always on call unless they mark themselves out.
            </div>
          </div>
        </label>
        <label className="settings-radio">
          <input type="radio" name="availabilityModel"
            checked={val('availabilityModel') === 'opt-in'}
            onChange={() => setAvailabilityModel('opt-in')}
            disabled={savingKey === 'availabilityModel'} />
          <div>
            <div className="settings-radio-label">Opt-in (recommended for rotating shifts)</div>
            <div className="settings-radio-help">
              Responders are off call by default and must add their available dates.
            </div>
          </div>
        </label>
        {savedKey === 'availabilityModel' && <span className="settings-saved">Saved</span>}
      </section>

      <section className="settings-section">
        <h3>Weekly confirmation</h3>
        <p className="settings-help">
          Monday-morning popup asking responders to confirm their week's shifts.
        </p>
        <label className="settings-toggle">
          <input type="checkbox"
            checked={!!val('weeklyConfirmationEnabled')}
            onChange={(e) => updateOne('weeklyConfirmationEnabled', e.target.checked)}
            disabled={savingKey === 'weeklyConfirmationEnabled'} />
          <span>Enable Monday-morning confirmation popup</span>
        </label>
        {savedKey === 'weeklyConfirmationEnabled' && <span className="settings-saved">Saved</span>}
      </section>

      <section className="settings-section">
        <h3>Coverage alerts</h3>
        <p className="settings-help">
          Warns when an upcoming gap appears in skill coverage.
        </p>
        <label className="settings-toggle">
          <input type="checkbox"
            checked={!!val('weeklyDigestEmailEnabled')}
            onChange={(e) => updateOne('weeklyDigestEmailEnabled', e.target.checked)}
            disabled={savingKey === 'weeklyDigestEmailEnabled'} />
          <span>Email me a weekly coverage digest (Monday 8am)</span>
        </label>
        {savedKey === 'weeklyDigestEmailEnabled' && <span className="settings-saved">Saved</span>}
        <label className="settings-toggle">
          <input type="checkbox"
            checked={!!val('gapPushEnabled')}
            onChange={(e) => updateOne('gapPushEnabled', e.target.checked)}
            disabled={savingKey === 'gapPushEnabled'} />
          <span>Push notify me if a gap appears in the next 24 hours</span>
        </label>
        {savedKey === 'gapPushEnabled' && <span className="settings-saved">Saved</span>}
      </section>

      <section className="settings-section">
        <h3>Requester feedback</h3>
        <p className="settings-help">
          When a customer rates a closed ticket (1-5 stars + comment), the rating
          is sent to this email. Defaults to the admin email on file. Use a team
          inbox like <em>ops@yourcompany.com</em> if multiple people should see feedback.
        </p>
        <input
          type="email"
          value={val('feedbackEmail') || ''}
          onChange={(e) => updateOne('feedbackEmail', e.target.value)}
          disabled={savingKey === 'feedbackEmail'}
          placeholder="(leave blank to use admin email)"
          className="ra-input"
          style={{ width: '100%', maxWidth: 360 }}
          maxLength={254}
        />
        {savedKey === 'feedbackEmail' && <span className="settings-saved">Saved</span>}
      </section>

      <section className="settings-section">
        <h3>
          Reception forwarding
          {!quietHoursUnlocked && (
            <span style={{
              marginLeft: 8, fontSize: 12, fontWeight: 600,
              padding: '2px 8px', borderRadius: 999,
              background: '#fef3c7', color: '#92400e',
            }}>Enterprise</span>
          )}
        </h3>
        <p className="settings-help">
          When a new ticket comes in during the reception hours you set below
          (in your vendor timezone), also notify a reception desk in parallel with
          the on-call rotation. <strong>Urgent ringing tickets always go through</strong> —
          reception is a human safety net <em>in addition</em> to the normal escalation
          chain, never instead.
        </p>

        {!quietHoursUnlocked ? (
          <div className="placeholder" style={{ padding: 16, marginTop: 8 }}>
            <strong>Enterprise feature.</strong> Upgrade to forward incoming tickets
            to a reception phone / email during your office hours.
            <div style={{ marginTop: 8 }}>
              <a href="/pricing" className="auth-link">See plans</a>
            </div>
          </div>
        ) : (
          <>
            <div>
              <strong style={{ fontSize: 14 }}>Forward to reception</strong>
              <p className="settings-help" style={{ marginTop: 4 }}>
                Optional. When a new ticket comes in during the reception hours you
                set below (in your vendor timezone), also notify a reception
                phone/email. Two layers:
              </p>
              <ul style={{ marginTop: 4, marginBottom: 8, paddingLeft: 20, fontSize: 13, color: 'var(--muted)' }}>
                <li><strong>At intake:</strong> SMS and/or email is sent immediately so reception is aware in parallel with the on-call rotation.</li>
                <li><strong>Last resort:</strong> if the entire escalation chain exhausts without acceptance, we ring the reception phone as a human safety net (Twilio required).</li>
              </ul>
              <p className="settings-help" style={{ marginTop: 4 }}>
                Your responders are still paged via the normal escalation chain -
                reception is <em>in addition</em>, never instead.
              </p>

              <label htmlFor="qh-fwd-phone" style={{ display: 'block', marginTop: 8, fontSize: 13 }}>Forward phone (SMS)</label>
              <input id="qh-fwd-phone" type="tel"
                placeholder="+1 415 555 1234 (or local format)"
                value={val('quietHours').forwardPhone || ''}
                onChange={(e) => updateOne('quietHours', { ...val('quietHours'), forwardPhone: e.target.value })}
                disabled={savingKey === 'quietHours'}
                style={{ width: '100%', maxWidth: 320, marginTop: 4 }} />

              <label htmlFor="qh-fwd-email" style={{ display: 'block', marginTop: 12, fontSize: 13 }}>Forward email</label>
              <input id="qh-fwd-email" type="email"
                placeholder="reception@yourcompany.com"
                value={val('quietHours').forwardEmail || ''}
                onChange={(e) => updateOne('quietHours', { ...val('quietHours'), forwardEmail: e.target.value })}
                disabled={savingKey === 'quietHours'}
                style={{ width: '100%', maxWidth: 320, marginTop: 4 }} />
              <p className="settings-help" style={{ marginTop: 6, fontSize: 12 }}>
                Leave either field blank to disable that channel. The SMS half requires
                Twilio to be wired up.
              </p>

              <label style={{ display: 'block', marginTop: 14, fontSize: 13, fontWeight: 600 }}>
                Forward to reception during these hours
              </label>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14 }}>From</span>
                <select
                  value={val('quietHours').forwardStart ?? 8}
                  onChange={(e) => updateOne('quietHours', { ...val('quietHours'), forwardStart: parseInt(e.target.value, 10) })}
                  disabled={savingKey === 'quietHours'}
                  className="settings-select" style={{ maxWidth: 120 }}>
                  {HOURS_24.map((h) => <option key={h} value={h}>{formatHour(h)}</option>)}
                </select>
                <span style={{ fontSize: 14 }}>to</span>
                <select
                  value={val('quietHours').forwardEnd ?? 18}
                  onChange={(e) => updateOne('quietHours', { ...val('quietHours'), forwardEnd: parseInt(e.target.value, 10) })}
                  disabled={savingKey === 'quietHours'}
                  className="settings-select" style={{ maxWidth: 120 }}>
                  {HOURS_24.map((h) => <option key={h} value={h}>{formatHour(h)}</option>)}
                </select>
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                  {(() => {
                    const qh = val('quietHours');
                    const s = qh.forwardStart ?? 8;
                    const e = qh.forwardEnd ?? 18;
                    if (s === e) return '(forwards 24/7)';
                    if (s > e) return '(window wraps past midnight)';
                    return '(reception notified for tickets in this window)';
                  })()}
                </span>
              </div>
              <p className="settings-help" style={{ marginTop: 6, fontSize: 12 }}>
                These hours are independent of quiet hours. A ticket arriving inside this
                window (vendor timezone) triggers the reception notice in parallel with the
                on-call rotation.
              </p>
            </div>

            {savedKey === 'quietHours' && <span className="settings-saved">Saved</span>}
          </>
        )}
      </section>

      <section className="settings-section">
        <h3>Vendor timezone</h3>
        <p className="settings-help">
          All date math (shift boundaries, weekly-confirmation timing, daily coverage
          recompute) uses this timezone.
        </p>
        <select value={currentTz}
          onChange={(e) => updateOne('timezone', e.target.value)}
          disabled={savingKey === 'timezone'}
          className="settings-select">
          <option value="" disabled>Choose a timezone...</option>
          {getTimezoneList().map((tz) => (<option key={tz} value={tz}>{tz}</option>))}
        </select>
        {!currentTz && browserTz && (
          <button type="button" className="settings-suggest-btn"
            onClick={() => updateOne('timezone', browserTz)}
            disabled={savingKey === 'timezone'}>
            Use my browser timezone ({browserTz})
          </button>
        )}
        {savedKey === 'timezone' && <span className="settings-saved">Saved</span>}
      </section>
    </>
  );
}

function PlanIndicator() {
  const { tier, loading } = useTier();
  if (loading) return null;
  if (tier.isInternal) {
    return (
      <div className="plan-indicator plan-indicator-pilot">
        <div className="plan-indicator-row">
          <TierBadge />
          <span className="plan-indicator-text">
            You have full feature access during the early-access period.
            <Link to="/pricing"> View pricing</Link>
          </span>
        </div>
      </div>
    );
  }
  return (
    <div className="plan-indicator">
      <div className="plan-indicator-row">
        <TierBadge />
        <span className="plan-indicator-text">
          {tier.price} {tier.priceSub}. <Link to="/pricing">Compare plans</Link>
        </span>
      </div>
    </div>
  );
}
